import type { ChatClient, GoalImpact } from "@/src/features/api/ChatApi";
import type {
  OnboardingV3GenerateRequestBody,
  OnboardingV3GenerateResponse,
  OnboardingV3GoalType,
  OnboardingV3PlanPreview,
} from "./onboardingV3Types";
import { normalizeFitnessScore } from "./normalizeFitnessScore";
import { normalizePlanImpact } from "./normalizePlanImpact";

const withRequestHeaders = (requestHeaders: Record<string, string>) => ({
  headers: requestHeaders,
});

export const ONBOARDING_V3_MIN_LOADING_MS = 3000;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export type OnboardingV3ApiErrorCode =
  | "UNAVAILABLE"
  | "INVALID_INPUT"
  | "GENERATION_FAILED"
  | "PREVIEW_TIMEOUT"
  | "PREVIEW_FAILED";

export class OnboardingV3ApiError extends Error {
  readonly code: OnboardingV3ApiErrorCode;

  constructor(code: OnboardingV3ApiErrorCode, message?: string) {
    super(message ?? code);
    this.code = code;
  }
}

const parseApiError = (
  status: number,
  body: unknown,
): OnboardingV3ApiErrorCode => {
  const errorField =
    typeof body === "object" && body !== null && "error" in body
      ? String((body as { error?: string }).error)
      : "";

  if (status === 400 || errorField === "INVALID_INPUT") {
    return "INVALID_INPUT";
  }
  if (status === 408 || errorField === "PREVIEW_TIMEOUT") {
    return "PREVIEW_TIMEOUT";
  }
  if (errorField === "GENERATION_FAILED") {
    return "GENERATION_FAILED";
  }
  if (errorField === "PREVIEW_FAILED") {
    return "PREVIEW_FAILED";
  }
  return status >= 500 ? "GENERATION_FAILED" : "UNAVAILABLE";
};

export async function runOnboardingV3GenerateRequest(
  chatApi: ChatClient<unknown> | undefined,
  requestHeaders: Record<string, string>,
  body: OnboardingV3GenerateRequestBody,
): Promise<OnboardingV3GenerateResponse> {
  if (!chatApi) {
    throw new OnboardingV3ApiError("UNAVAILABLE", "chatApi unavailable");
  }

  const [, response] = await Promise.all([
    sleep(ONBOARDING_V3_MIN_LOADING_MS),
    chatApi.onboarding.v3GenerateCreate(
      {
        ...body,
        context: body.context ?? {},
      },
      withRequestHeaders(requestHeaders),
    ),
  ]);

  if (response.error) {
    const status = response.status ?? 500;
    throw new OnboardingV3ApiError(
      parseApiError(status, response.error),
      JSON.stringify(response.error),
    );
  }

  const {
    previewId,
    resultValues,
    fitnessScore: rawFitnessScore,
  } = response.data ?? {};
  if (!previewId || !resultValues) {
    throw new OnboardingV3ApiError(
      "GENERATION_FAILED",
      "Missing previewId or resultValues",
    );
  }

  const fitnessScore = normalizeFitnessScore(rawFitnessScore);

  return {
    previewId,
    resultValues: {
      goalType: (resultValues.goalType ??
        body.goalType) as OnboardingV3GoalType,
      values: (resultValues.values ?? {}) as Record<string, unknown>,
    },
    ...(fitnessScore ? { fitnessScore } : {}),
  };
}

export async function runOnboardingPlanPreviewRequest(
  chatApi: ChatClient<unknown> | undefined,
  requestHeaders: Record<string, string>,
  previewId: string,
): Promise<OnboardingV3PlanPreview> {
  if (!chatApi) {
    throw new OnboardingV3ApiError("UNAVAILABLE", "chatApi unavailable");
  }

  const response = await chatApi.onboarding.v3PlanPreviewDetail(
    encodeURIComponent(previewId),
    withRequestHeaders(requestHeaders),
  );

  if (response.error) {
    const status = response.status ?? 500;
    throw new OnboardingV3ApiError(
      parseApiError(status, response.error),
      JSON.stringify(response.error),
    );
  }

  const data = response.data;
  if (!data?.draftId) {
    throw new OnboardingV3ApiError("PREVIEW_FAILED", "Missing draftId");
  }

  return {
    title: data.goalStatement ?? (data as { title?: string }).title ?? "",
    summary: data.summary ?? "",
    planItems: (data.planItems ?? []).map((item) => ({
      title: item.title ?? "",
      description: item.description ?? "",
      visible: item.visible ?? false,
    })),
    draftId: data.draftId,
    impact: normalizePlanImpact((data as { impact?: GoalImpact }).impact),
  } satisfies OnboardingV3PlanPreview;
}
